import React, { useEffect, useRef, useState, type ReactElement } from 'react';

type Props = {
  title?: string;
  lines: string[];
  active: boolean;
  error?: string;
  emptyHint?: string;
};

export default function LambdaLogStreamPanel({
  title = 'Live output',
  lines,
  active,
  error,
  emptyHint = 'Waiting for log output…',
}: Props): ReactElement {
  const scrollRef = useRef<HTMLPreElement | null>(null);
  const [stickToBottom, setStickToBottom] = useState(true);

  useEffect(() => {
    if (!stickToBottom || !scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [lines, stickToBottom]);

  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700">
      <div className="flex items-center justify-between border-b border-slate-200 px-2 py-1 dark:border-slate-700">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {title}
          {active ? (
            <span className="ml-2 inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-current" />
              live
            </span>
          ) : null}
        </span>
        <span className="text-[10px] text-slate-400">{lines.length} line(s)</span>
      </div>
      <pre
        ref={scrollRef}
        onScroll={(e) => {
          const el = e.currentTarget;
          const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
          setStickToBottom(atBottom);
        }}
        className="max-h-48 overflow-auto bg-slate-950 p-2 font-mono text-[11px] leading-relaxed text-slate-100"
      >
        {lines.length < 1 ? emptyHint : lines.join('\n')}
      </pre>
      {error ? <p className="px-2 py-1 text-xs text-rose-600 dark:text-rose-300">{error}</p> : null}
    </div>
  );
}
